import { useRef, useState } from "react";
import {
  detectLayersFromFiles,
  type DetectedLayer,
  type LayerDetectionError,
} from "../model/gis/detectLayers.ts";
import {
  IMPORT_TARGETS_BY_GEOMETRY,
  IMPORT_TARGET_LABEL_KEY,
  type ImportTargetSection,
} from "../model/gis/importTargets.ts";
import { defaultValueFor, importColumnsFor } from "../model/gis/importSchema.ts";
import { importLayer, type FieldMapping } from "../model/gis/importLayer.ts";
import { resolveImportTargetCrs } from "../model/gis/crsResolution.ts";
import { reprojectXY } from "../model/geo/reproject.ts";
import { pluralText, useT } from "../i18n/hooks.ts";
import { Dialog } from "./Dialog.tsx";
import { DialogActions } from "./DialogActions.tsx";
import { FolderOpenIcon } from "./FolderOpenIcon.tsx";

const SKIP = "";

function guessNameProperty(layer: DetectedLayer): string {
  const exact = layer.propertyNames.find((p) => p.toLowerCase() === "name");
  return exact ?? layer.propertyNames[0] ?? "";
}

export function GisImportDialog({
  rawSections,
  currentCrs,
  projectIsEmpty,
  onImport,
  onClose,
}: {
  rawSections: ReadonlyMap<string, string[][]>;
  /** null when the project isn't georeferenced. */
  currentCrs: string | null;
  /** True for a brand-new project with nothing drawn yet. */
  projectIsEmpty: boolean;
  onImport: (
    raw: ReadonlyMap<string, string[][]>,
    placeholderNames: string[],
    adoptedCrs?: string
  ) => void;
  onClose: () => void;
}) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [layers, setLayers] = useState<DetectedLayer[] | null>(null);
  const [errors, setErrors] = useState<LayerDetectionError[]>([]);
  const [loading, setLoading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  // Keyed by fileName rather than array index, since a re-pick can add or
  // remove rows — an index would silently point at the wrong layer.
  const [assignments, setAssignments] = useState<Record<string, ImportTargetSection | typeof SKIP>>(
    {}
  );
  const [nameMapping, setNameMapping] = useState<Record<string, string>>({});
  const [fieldMapping, setFieldMapping] = useState<Record<string, FieldMapping>>({});
  const [summary, setSummary] = useState<Record<
    string,
    { created: number; skipped: number }
  > | null>(null);
  const [placeholderCount, setPlaceholderCount] = useState(0);
  // Guards against a slower earlier folder scan overwriting a faster later
  // one: re-picking a different folder before the first scan finishes used
  // to let whichever detectLayersFromFiles call resolved LAST win.
  const requestId = useRef(0);

  // No layer can be assigned at all while this is true (the "Import as"
  // cell shows "—"), which alone keeps `assignments` empty and so already
  // disables the field-mapping section and the Import button too.
  const blockedByProjectCrs = currentCrs == null && !projectIsEmpty;

  async function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const id = ++requestId.current;
    setLoading(true);
    setReadError(null);
    try {
      const result = await detectLayersFromFiles(Array.from(fileList));
      if (id !== requestId.current) return; // superseded by a newer pick
      setLayers(result.layers);
      setErrors(result.errors);
      setAssignments({});
      setNameMapping({});
      setFieldMapping({});
      setSummary(null);
      setPlaceholderCount(0);
    } catch (err) {
      if (id !== requestId.current) return; // superseded by a newer pick
      setReadError(err instanceof Error ? err.message : String(err));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }

  function setAssignment(fileName: string, value: string) {
    setAssignments((current) => ({
      ...current,
      [fileName]: value as ImportTargetSection | typeof SKIP,
    }));
  }

  function setColumnMapping(fileName: string, column: string, property: string) {
    setFieldMapping((current) => ({
      ...current,
      [fileName]: { ...current[fileName], [column]: property },
    }));
  }

  function handleImport() {
    if (!layers) return;
    const layersBeingImported = layers
      .filter((layer) => assignments[layer.fileName])
      .map((layer) => ({
        sourceCrs: layer.sourceCrs,
        section: assignments[layer.fileName] as ImportTargetSection,
      }));
    const resolution = resolveImportTargetCrs(currentCrs, projectIsEmpty, layersBeingImported);
    // Defensive: blockedByProjectCrs already prevents any assignment at all
    // whenever this would otherwise be null.
    if (resolution.targetProj4 == null) return;

    let nextRaw = rawSections;
    const nextSummary: Record<string, { created: number; skipped: number }> = {};
    const allPlaceholderNames: string[] = [];
    for (const layer of layers) {
      const section = assignments[layer.fileName];
      if (!section || layer.sourceCrs == null) continue;
      const nameProperty = nameMapping[layer.fileName] ?? guessNameProperty(layer);
      if (!nameProperty) continue;
      const sourceCrs = layer.sourceCrs;
      const targetProj4 = resolution.targetProj4;
      const result = importLayer(
        nextRaw,
        layer,
        section,
        nameProperty,
        fieldMapping[layer.fileName] ?? {},
        (x, y) => reprojectXY(x, y, sourceCrs, targetProj4)
      );
      nextRaw = result.raw;
      nextSummary[layer.fileName] = { created: result.createdCount, skipped: result.skippedCount };
      allPlaceholderNames.push(...result.placeholderNames);
    }
    onImport(nextRaw, allPlaceholderNames, resolution.adoptedProj4);
    setSummary(nextSummary);
    setPlaceholderCount(allPlaceholderNames.length);
  }

  return (
    <Dialog title={t("gisImport.title")} onClose={onClose} wide>
      <p className="gis-import-hint">{t("gisImport.hint")}</p>
      {blockedByProjectCrs && (
        <p className="gis-import-warning">{t("gisImport.unknownProjectCrsBlocked")}</p>
      )}
      <button className="button button-primary" onClick={() => inputRef.current?.click()}>
        <FolderOpenIcon />
        {t("gisImport.pickFolder")}
      </button>
      <input
        ref={inputRef}
        type="file"
        // webkitdirectory isn't in React's own DOM input typings; chosen
        // over the File System Access API's showDirectoryPicker for
        // broader browser support.
        {...{ webkitdirectory: "" }}
        multiple
        className="landing-file-input"
        onChange={(e) => void handleFiles(e.target.files)}
      />

      {loading && <p className="gis-import-status">{t("gisImport.reading")}</p>}
      {readError && (
        <p className="gis-import-status gis-import-error" role="alert">
          {t("gisImport.readFailed", { message: readError })}
        </p>
      )}

      {layers && (
        <div className="gis-import-results">
          {layers.length === 0 && errors.length === 0 && (
            <p className="gis-import-status">{t("gisImport.noneFound")}</p>
          )}
          {layers.length > 0 && (
            <table className="gis-import-table">
              <thead>
                <tr>
                  <th>{t("gisImport.columnFile")}</th>
                  <th>{t("gisImport.columnGeometry")}</th>
                  <th>{t("gisImport.columnFeatures")}</th>
                  <th>{t("gisImport.columnType")}</th>
                </tr>
              </thead>
              <tbody>
                {layers.map((layer) => {
                  const targets = layer.geometryType
                    ? IMPORT_TARGETS_BY_GEOMETRY[layer.geometryType]
                    : [];
                  return (
                    <tr key={layer.fileName}>
                      <td>{layer.fileName}</td>
                      <td>
                        {layer.geometryType ?? (
                          <span className="gis-import-warning">{t("gisImport.mixedGeometry")}</span>
                        )}
                      </td>
                      <td>{layer.featureCount}</td>
                      <td>
                        {layer.blockedReason ? (
                          <span className="gis-import-warning">{t(layer.blockedReason)}</span>
                        ) : blockedByProjectCrs || targets.length === 0 ? (
                          "—"
                        ) : (
                          <select
                            aria-label={t("gisImport.columnType")}
                            value={assignments[layer.fileName] ?? SKIP}
                            onChange={(e) => setAssignment(layer.fileName, e.target.value)}
                          >
                            <option value={SKIP}>{t("gisImport.skip")}</option>
                            {targets.map((section) => (
                              <option key={section} value={section}>
                                {t(IMPORT_TARGET_LABEL_KEY[section])}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {errors.length > 0 && (
            <ul className="gis-import-errors">
              {errors.map((error) => (
                <li key={error.fileName}>
                  {t("gisImport.layerReadFailed", {
                    fileName: error.fileName,
                    message: error.message,
                  })}
                </li>
              ))}
            </ul>
          )}

          {layers
            .filter((layer) => assignments[layer.fileName])
            .map((layer) => {
              const section = assignments[layer.fileName] as ImportTargetSection;
              const columns = importColumnsFor(section);
              const nameProperty = nameMapping[layer.fileName] ?? guessNameProperty(layer);
              return (
                <fieldset key={layer.fileName} className="gis-import-mapping">
                  <legend>
                    {layer.fileName} → {t(IMPORT_TARGET_LABEL_KEY[section])}
                  </legend>
                  <label className="gis-import-mapping-field">
                    {t("gisImport.nameField")}
                    <select
                      value={nameProperty}
                      onChange={(e) =>
                        setNameMapping((current) => ({
                          ...current,
                          [layer.fileName]: e.target.value,
                        }))
                      }
                    >
                      {layer.propertyNames.map((property) => (
                        <option key={property} value={property}>
                          {property}
                        </option>
                      ))}
                    </select>
                  </label>
                  {columns.map((column) => (
                    <label key={column} className="gis-import-mapping-field">
                      {column}
                      <select
                        value={fieldMapping[layer.fileName]?.[column] ?? SKIP}
                        onChange={(e) => setColumnMapping(layer.fileName, column, e.target.value)}
                      >
                        <option value={SKIP}>
                          {t("gisImport.useDefault", { value: defaultValueFor(column) })}
                        </option>
                        {layer.propertyNames.map((property) => (
                          <option key={property} value={property}>
                            {property}
                          </option>
                        ))}
                      </select>
                    </label>
                  ))}
                </fieldset>
              );
            })}

          {summary && (
            <ul className="gis-import-summary">
              {Object.entries(summary).map(([fileName, s]) => (
                <li key={fileName}>
                  {t("gisImport.summaryLine", {
                    file: fileName,
                    created: String(s.created),
                    skipped: String(s.skipped),
                  })}
                </li>
              ))}
              {placeholderCount > 0 && (
                <li className="gis-import-warning">
                  {pluralText(
                    t,
                    placeholderCount,
                    "gisImport.placeholderSummaryOne",
                    "gisImport.placeholderSummary"
                  )}
                </li>
              )}
            </ul>
          )}
        </div>
      )}

      <DialogActions
        primary={
          layers && layers.some((layer) => assignments[layer.fileName])
            ? { label: t("gisImport.importButton"), onClick: handleImport }
            : undefined
        }
        onClose={onClose}
        closeLabel={summary ? t("common.done") : t("common.cancel")}
      />
    </Dialog>
  );
}
